import {
  ReviewProfileCheckpoint,
  ReviewProfileSource,
  ReviewProfileTier,
} from '../../../api/structs/reviewProfile';
import { ResolvedReviewCapabilities } from './resolveReviewCapabilities';

/** The source ids the capability fields switch, as the engine names them. */
const SOURCE_STORY = 'story';
const SOURCE_SCOPE = 'scope';
const SOURCE_DESCRIPTIONS = 'descriptions';
const SOURCE_ATTEMPTS = 'attempts';
const SOURCE_GUIDANCE = 'guidance';

/**
 * The engine's own wording for how far the judge looks back. Scope 3 is not
 * "previous 3 scenes": it is a budgeted selection over the whole run so far,
 * so it gets its own words rather than a count that would be wrong.
 */
const scopeDetail = (scope: number): string | undefined => {
  if (scope <= 0) return undefined;
  if (scope >= 3) return 'the story so far';
  return `previous ${scope} ${scope === 1 ? 'scene' : 'scenes'}`;
};

const overlaySource = (
  source: ReviewProfileSource,
  resolved: ResolvedReviewCapabilities,
  role: string
): ReviewProfileSource => {
  switch (source.id) {
    case SOURCE_STORY:
      return { ...source, on: resolved.story };
    case SOURCE_SCOPE: {
      // The engine only derives this source's `scope` for a still/still_pair
      // checkpoint (review_profile_slots.go) — a text checkpoint never reads
      // neighbouring scenes, so the switch must not light up there just
      // because the account/template capability says scene_scope > 0.
      const on = resolved.scene_scope > 0 && (role === 'still' || role === 'still_pair');
      return {
        ...source,
        on,
        // An off node must not keep showing a stale "previous N scenes" —
        // only a real scope > 0 (and a role that reads it) gets a detail.
        detail: on ? scopeDetail(resolved.scene_scope) : undefined,
      };
    }
    case SOURCE_DESCRIPTIONS:
      return { ...source, on: resolved.descriptions };
    case SOURCE_ATTEMPTS:
      return { ...source, on: resolved.attempt_history };
    case SOURCE_GUIDANCE:
      return { ...source, on: resolved.guidance.trim() !== '' };
    default:
      return source;
  }
};

const overlayTier = (
  tier: ReviewProfileTier,
  resolved: ResolvedReviewCapabilities,
  role: string,
  running: boolean
): ReviewProfileTier => {
  if (tier.id !== 'pair') return tier;
  return {
    ...tier,
    // The server derives `Enabled = running && PairJudge && role ==
    // still_pair` (review_profile_policies.go), where `running` is the same
    // "mode lets tiers run" state the check tier's own `enabled` carries —
    // NOT this tier's own already-baked-in `enabled`. Anding against the pair
    // tier's own value (the old bug) meant a saved `pair_judge: false` could
    // never be switched on by this overlay: `tier.enabled` was already false
    // and nothing here could flip it back.
    enabled: running && resolved.pair_judge && role === 'still_pair',
    provider: resolved.pair.provider || tier.provider,
    model: resolved.pair.model || tier.model,
  };
};

/**
 * Redraws one checkpoint's reviewer picture from capabilities the user has not
 * saved yet.
 *
 * `ReviewProfileCheckpoint` is derived by the BACKEND from saved state, so
 * without this the picture beside the form would lag a save + refetch behind
 * every keystroke. Rather than re-deriving the whole view in the browser (a
 * second source of truth for a shape the engine owns), this overlays only the
 * capability fields onto the parts of the picture they ALREADY control here:
 * five source nodes, the pair tier, and the two check lists. Everything else —
 * slots, images, policies, gates, and any node the backend derives that no
 * case below names (the cast switch among them) — still comes from the
 * backend's own answer, so an unsaved toggle for one of those shows up on the
 * next refetch rather than instantly.
 *
 * `enabled` on the pair tier is ANDed against `running` (the check tier's own
 * `enabled`, i.e. "does this mode let tiers run at all") rather than against
 * the pair tier's own baked-in `enabled`: a mode that switches the reviewer
 * off must still leave every tier disabled, but a capability toggle must be
 * able to promise a judge call the mode itself has not already ruled out —
 * including turning the pair tier ON from a saved `pair_judge: false`.
 */
export const applyReviewOverrides = (
  checkpoint: ReviewProfileCheckpoint,
  resolved: ResolvedReviewCapabilities
): ReviewProfileCheckpoint => {
  // The same "mode lets tiers run" state the check tier's `enabled` carries —
  // mirrors the backend's `running` in review_profile_policies.go.
  const running = checkpoint.tiers.find((tier) => tier.id === 'check')?.enabled ?? false;
  return {
    ...checkpoint,
    sources: checkpoint.sources.map((source) => overlaySource(source, resolved, checkpoint.role)),
    tiers: checkpoint.tiers.map((tier) => overlayTier(tier, resolved, checkpoint.role, running)),
    checks: {
      ...checkpoint.checks,
      fixed: resolved.fixed_checks
        .map((spec) => (spec.check || '').trim())
        .filter((check) => check !== ''),
      lenses: [...resolved.lenses],
    },
  };
};

const contextSource = (
  id: string,
  label: string,
  on: boolean,
  detail?: string
): ReviewProfileSource => ({ id, label, lane: 'ctx', on, available: true, detail });

/** What the account pane already knows about its own tiers and gates. */
export interface ReviewBaselineInput {
  check?: { provider?: string; model?: string };
  confirm?: { provider?: string; model?: string };
  confidence_gate?: number;
  severity_gate?: string;
  max_attempts?: number;
}

/**
 * A two-frame scene as the ACCOUNT settings alone would review it.
 *
 * Settings → Reviewer has no template to read a profile for, so it draws this
 * instead: the same shape the backend emits, trimmed to the nodes the account
 * capabilities actually move, and fed through the same overlay as a real one.
 * It is an illustration of the baseline, not a claim about any pipeline — which
 * is why the checkpoint id says so.
 */
export const baselineReviewCheckpoint = (
  baseline: ReviewBaselineInput = {}
): ReviewProfileCheckpoint => ({
  checkpoint_id: 'a typical scene',
  type: 'generator',
  reviewed: true,
  role: 'still_pair',
  judge_template_id: 'judge-still-v1',
  judge_template_found: true,
  slots: [],
  sources: [
    contextSource('candidate', 'the candidate', true, 'what is under review'),
    contextSource('prompts', 'generation prompt', true),
    contextSource('story', 'story', false),
    contextSource('scope', 'neighbouring scenes', false),
    contextSource('references', 'reference images', true),
    contextSource('attempts', 'earlier attempts', false),
    contextSource(
      'descriptions',
      'reference descriptions',
      false,
      "reference labels carry each image's saved description"
    ),
    contextSource('guidance', 'pipeline guidance', false),
    { id: 'checks', label: 'acceptance criteria', lane: 'chk', on: true, available: true },
  ],
  images: {
    candidate: 'both frames',
    max_references: 4,
    continuity_ref: false,
    evidence_enabled: true,
    evidence_max: 2,
  },
  checks: { source: 'planner', per_frame: true },
  tiers: [
    { id: 'check', ...baseline.check, enabled: true, available: true, inherited: false },
    { id: 'confirm', ...baseline.confirm, enabled: true, available: true, inherited: false },
    // The pair judge follows confirm until some layer names a model of its own.
    { id: 'pair', ...baseline.confirm, enabled: true, available: true, inherited: true },
  ],
  gates: {
    confidence: baseline.confidence_gate ?? 0,
    severity: baseline.severity_gate || 'blocking',
    max_attempts: baseline.max_attempts ?? 0,
    inherited: false,
  },
  policies: [],
});

export default applyReviewOverrides;
